import { AccentBox, DraggableList, Skeleton } from 'components/common';
import { ViewToggleOption } from 'components/core';
import _ from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ALL_FILTERS } from 'utils/constants/Filters';
import { FilterContext, ListContext, ViewContext } from 'utils/context';
import { usePermissions } from 'utils/hooks/';
import { Tcreate, Tdelete } from 'utils/hooks/useSavedViews/useSavedViews';

import {
  Box,
  Button,
  Flex,
  Icon,
  Overlay,
  Popover,
  Typography,
  useOverlayPosition,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';
import { SavedView as TSavedView } from '@project44-models/shipment-list';

import { formatCount } from '../ViewToggleOption/utils';
import { EntityOption } from './types';
import { create, remove, update } from './utils';

const styles = {
  content: {
    borderRadius: '$small',
    maxHeight: '600px',
    overflow: 'scroll',
    width: '400px',
  },
  button: {
    height: 'auto',
    width: '215px',
    '&:disabled': {
      opacity: 1,
    },
    '&:hover': {
      bgColor: '$palette-blue-800',
    },
  },
  name: {
    color: '$text-contrast',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  list: {
    color: '$text-tertiary',
    padding: 16,
    paddingBottom: 8,
  },
  skeleton: {
    borderRadius: '2px',
    height: '58px',
    width: '215px',
  },
  manage: {
    color: '$text-secondary',
    height: 56,
    justifyContent: 'flex-start',
    paddingLeft: 16,
    width: '100%',
  },
  viewName: {
    height: 58,
    whiteSpace: 'nowrap',
    maxWidth: 335,
    '@media (max-width: 1500px)': {
      maxWidth: 215,
    },
    '@media (max-width: 1200px)': {
      maxWidth: 150,
    },
  },
};

interface IViewToggle {
  totalOrders?: number;
  totalShipments?: number;
}

function ViewToggle({ totalOrders, totalShipments }: IViewToggle) {
  const { t } = useTranslation();
  const {
    flags: { featureExc2241 },
    permissions: { canViewOrders },
  } = usePermissions();
  const { ordersData, shipmentsData, isLoadingOrders, isLoadingShipments } =
    useContext(ListContext);
  const { filters, clearFilters, setFiltersKey } = useContext(FilterContext);
  const {
    savedOrders,
    savedOrders: {
      data: { views: orderViews },
      isLoading: loadingOrders,
      sortViews: sortOrders,
    },
    savedShipments,
    savedShipments: {
      data: { views: shipmentViews },
      isLoading: loadingShipments,
      sortViews: sortShipments,
    },
    viewType,
  } = useContext(ViewContext);

  const isOrder = viewType === 'order';
  const [selected, setSelected] = useState<EntityOption>();
  const [isOpen, setIsOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const isLoading = loadingOrders || loadingShipments;
  const canToggle = canViewOrders || !!shipmentViews.length;
  const isCountLoading = isOrder ? isLoadingOrders : isLoadingShipments;
  const viewCount = isOrder
    ? ordersData?.paginationInfo?.total
    : shipmentsData?.paginationInfo?.total;
  const orderUnit = t(`core.${viewCount === 1 ? 'order' : 'orders'}`);
  const shipmentUnit = t(`core.${viewCount === 1 ? 'shipment' : 'shipments'}`);
  const countUnit = isOrder ? orderUnit : shipmentUnit;

  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const targetRef = useRef(null);
  const { overlayProps } = useOverlayPosition({
    isOpen,
    offset: 8,
    onClose: () => setIsOpen(false),
    overlayRef,
    placement: 'top left',
    targetRef,
  });

  const { createView, removeView } = useMemo(
    () => ({
      createView: (view: TSavedView, callback: Tcreate) => async () => {
        const res = await create(view, callback)();
        if (res?.views?.length)
          setFiltersKey('listView', res.views[0].id || '');
      },
      removeView: (view: TSavedView, callback: Tdelete, listView: string) => {
        return async () => {
          await remove(view, callback)();
          if (view.id === filters.listView) {
            clearFilters(ALL_FILTERS);
            setFiltersKey('listView', listView);
          }
        };
      },
    }),
    [clearFilters, filters, setFiltersKey],
  );

  const shipmentOptions: EntityOption[] = useMemo(
    () => [
      {
        id: 'all-shipments',
        namespace: t('core.myShipments'),
        name: t('core.allShipments'),
        accentColor: '$palette-blue-500',
        listType: 'shipment',
        totalResults: totalShipments,
      },
      ...shipmentViews.map((view: TSavedView) => ({
        id: view.id as string,
        name: view.name,
        namespace: t('core.myShipments'),
        accentColor: '$palette-blue-500',
        listType: 'shipment',
        createView: createView(view, savedShipments.createView),
        deleteView: removeView(
          view,
          savedShipments.deleteView,
          'all-shipments',
        ),
        updateView: update(view, savedShipments.updateView),
      })),
    ],
    [createView, removeView, shipmentViews, savedShipments, t, totalShipments],
  );

  const orderOptions: EntityOption[] = useMemo(
    () => [
      {
        id: 'all-orders',
        namespace: t('core.myOrders'),
        name: t('core.allOrders'),
        accentColor: '$palette-pink-500',
        listType: 'order',
        totalResults: totalOrders,
      },
      ...orderViews.map((view: TSavedView) => ({
        id: view.id as string,
        name: view.name,
        namespace: t('core.myOrders'),
        accentColor: '$palette-pink-500',
        listType: 'order',
        createView: createView(view, savedOrders.createView),
        deleteView: removeView(view, savedOrders.deleteView, 'all-orders'),
        updateView: update(view, savedOrders.updateView),
      })),
    ],
    [createView, removeView, orderViews, savedOrders, t, totalOrders],
  );

  const Entities = useMemo(() => {
    const handleListView = (nextView: string) => {
      setIsOpen(false);
      if (filters.listView !== nextView) {
        setFiltersKey('listView', nextView);
      }
    };

    const mapEntity = ({ id, ...rest }: EntityOption) => (
      <ViewToggleOption
        key={id}
        isSelected={id === filters.listView}
        onClick={() => handleListView(id)}
        isEdit={isEdit}
        {...rest}
      />
    );

    const opts = _.compact([
      [t('core.myShipments'), shipmentOptions],
      canViewOrders && [t('core.myOrders'), orderOptions],
    ]) as Array<[string, EntityOption[]]>;

    return opts.map(([name, key]) => (
      <Box key={name} css={{ borderBottom: '1px solid $border-primary' }}>
        <Typography
          variant="subtextBold"
          as="div"
          css={styles.list}
          children={name}
        />
        {key.slice(0, 1).map(mapEntity)}
        <DraggableList
          onChange={key[0].listType === 'shipment' ? sortShipments : sortOrders}
          list={key.slice(1).map((entity: EntityOption) => ({
            id: entity.id,
            Component: mapEntity(entity),
          }))}
        />
      </Box>
    ));
  }, [
    canViewOrders,
    filters,
    isEdit,
    orderOptions,
    setFiltersKey,
    shipmentOptions,
    sortOrders,
    sortShipments,
    t,
  ]);

  useEffect(() => {
    const opts = [...shipmentOptions, ...orderOptions];
    const next = opts.find(({ id }) => id === filters.listView);
    setSelected(next || shipmentOptions[0]);
  }, [filters.listView, isLoading, orderOptions, shipmentOptions]);

  useEffect(() => {
    if (isEdit && !isOpen) setIsEdit(false);
  }, [isEdit, isOpen]);

  if (isLoading) {
    return <Skeleton variant="dark" sx={styles.skeleton} />;
  }

  if (featureExc2241) {
    return (
      <Flex align="center" gap="small" justify="between" css={styles.viewName}>
        <AccentBox bgColor={selected?.accentColor} />
        <Box css={{ flex: 1, textAlign: 'left' }}>
          <Typography
            variant="subtitle"
            as="div"
            css={styles.name}
            children={selected?.name}
          />
          <Typography
            variant="caption"
            as="div"
            css={{ color: '$palette-grey-300' }}
            children={
              isCountLoading ? (
                <Skeleton variant="dark" sx={{ height: 18, width: 75 }} />
              ) : (
                `${formatCount(viewCount) || 0} ${countUnit}`
              )
            }
          />
        </Box>
      </Flex>
    );
  }

  return (
    <Box ref={containerRef}>
      <Button
        aria-label={t('core.toggleSavedViews')}
        ref={targetRef}
        isDisabled={!canToggle}
        onPress={() => setIsOpen(!isOpen)}
        variant="tertiary"
        css={{
          ...styles.button,
          ...(isOpen && { bgColor: '$palette-blue-900' }),
        }}
      >
        <Flex
          align="center"
          gap="small"
          justify="between"
          css={{ width: '100%' }}
        >
          <AccentBox bgColor={selected?.accentColor} />
          <Box css={{ flex: 1, textAlign: 'left' }}>
            <Typography
              variant="caption"
              as="div"
              css={{ color: '$palette-grey-300' }}
              children={selected?.namespace}
            />
            <Typography
              variant="subtitle"
              as="div"
              css={styles.name}
              children={selected?.name}
            />
          </Box>
          {canToggle && (
            <Icon icon="expand_more" css={{ color: '$text-contrast' }} />
          )}
        </Flex>
      </Button>
      <Overlay isOpen={isOpen} containerRef={containerRef}>
        <Popover
          ref={overlayRef}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          {...overlayProps}
        >
          <Box>
            <Box css={styles.content}>{Entities}</Box>
            <Flex
              gap="medium"
              justify="end"
              css={{
                padding: isEdit ? '$small $medium' : '',
                borderTop: '1px solid $border-primary',
              }}
            >
              {isEdit ? (
                <Button
                  aria-label="Done Managing Saved Views"
                  variant="primary"
                  onPress={() => setIsEdit(false)}
                  children={t('global.done')}
                />
              ) : (
                <Button
                  aria-label="Manage Saved Views"
                  variant="tertiary"
                  css={styles.manage}
                  onPress={() => setIsEdit(true)}
                  children={t('core.manageViews')}
                />
              )}
            </Flex>
          </Box>
        </Popover>
      </Overlay>
    </Box>
  );
}

export default ViewToggle;
